import type { TabData } from './engine/schema'
import { historyFromFixture } from './engine/fixtureHistory'
import type { History, LaborMeta, View } from './engine/types'
import type { ApiHistoryPayload, ApiHistoryTab, ApiViewLaborLines } from './historyTypes'

const API_TABS = ['IS_RIVEROAKS', 'IS_HIGHLANDPARK', 'IS_GA', 'BS_FULL', 'CF_FULL'] as const
const SCHEMA_FIRST_ROW = 21

const VIEW_OVERLAY_LINE_KEYS = [
  'is.hourly_fte_boh',
  'is.hourly_fte_foh',
  'is.avg_hourly_rate_boh',
  'is.avg_hourly_rate_foh',
  'is.salaried_headcount',
  'is.avg_salary_annual',
  'is.boh',
  'is.foh',
] as const

const VIEW_KEYS: View[] = ['river_oaks', 'highland_park', 'ga']

function parseLaborMeta(payload: ApiHistoryPayload): LaborMeta | null {
  const raw = payload.labor_meta
  if (!raw) {
    return null
  }
  return {
    residual: raw.residual as LaborMeta['residual'],
    salaried_shares: raw.salaried_shares as LaborMeta['salaried_shares'],
    first_month: raw.first_month,
  }
}

/** Convert GET /history payload (`{ IS_RIVEROAKS: { dates, rows } }`) into engine History. */
export function historyFromApiPayload(
  payload: ApiHistoryPayload,
  lastClosedMonth: string,
): History {
  const tabs: Record<string, TabData> = {}
  for (const tabName of API_TABS) {
    const tab = payload[tabName] as ApiHistoryTab | undefined
    if (!tab || typeof tab !== 'object' || !('rows' in tab) || !tab.rows) {
      continue
    }
    const nHistory =
      tab.dates?.length ??
      Object.values(tab.rows)[0]?.values?.length ??
      0
    const rowNums = Object.keys(tab.rows)
      .map(Number)
      .filter((n) => Number.isFinite(n))
    const maxRow = rowNums.length > 0 ? Math.max(...rowNums) : SCHEMA_FIRST_ROW - 1
    const values: unknown[][] = []
    for (let row = SCHEMA_FIRST_ROW; row <= maxRow; row += 1) {
      values.push(tab.rows[String(row)]?.values ?? [])
    }
    const statusCells = Array(7 + nHistory).fill('')
    for (let i = 0; i < nHistory; i += 1) {
      statusCells[7 + i] = 'Actual'
    }
    tabs[tabName] = { values, status: [statusCells] }
  }
  const closed =
    lastClosedMonth ||
    (payload.IS_RIVEROAKS && typeof payload.IS_RIVEROAKS === 'object' && 'dates' in payload.IS_RIVEROAKS
      ? payload.IS_RIVEROAKS.dates?.at(-1)
      : undefined) ||
    ''
  const history = historyFromFixture({
    control: { last_closed_month: closed },
    tabs,
  })

  for (const view of VIEW_KEYS) {
    const viewLines = payload[view] as ApiViewLaborLines | undefined
    if (!viewLines) {
      continue
    }
    for (const lineKey of VIEW_OVERLAY_LINE_KEYS) {
      const values = viewLines[lineKey]
      if (!Array.isArray(values)) {
        continue
      }
      if (!history.values[view][lineKey]) {
        history.values[view][lineKey] = new Array(history.nHistory).fill(null)
      }
      for (let i = 0; i < history.nHistory && i < values.length; i += 1) {
        const raw = values[i]
        if (raw == null || raw === '') {
          continue
        }
        const parsed = Number(raw)
        history.values[view][lineKey]![i] =
          Number.isFinite(parsed) ? parsed : raw
      }
    }
  }

  history.laborMeta = parseLaborMeta(payload)
  return history
}
